import { KOMBO_NAME_PREFIX, MENU_PIZZA_BASE_PREFIX, komboHalvesOf, tt } from '@agos/shared'

/**
 * Reading a product name the way a cook needs to read it.
 *
 * An order line arrives as one string that the order builder assembled, and it
 * can carry three things at once:
 *
 *   `Margherita (mozzarella, rajčiny) [+ Šunka 2×]`
 *    ^name       ^kitchen description  ^what the customer added
 *
 * The kitchen card needs them apart: the name large, the description as
 * ingredient chips, and the extras merged into those chips with their counts so
 * a cook sees `2× Šunka` rather than "ham" twice in two different places.
 *
 * Ported from legacy `pages/kitchen.tsx`. Pure, and tested, because a parse
 * that loses the brackets loses what the customer paid extra for.
 */

export interface ParsedItemName {
  name: string
  /** The parenthesised ingredient list, when the builder put one there. */
  description: string | null
  /** The bracketed additions, verbatim — still one string. */
  extras: string | null
  isKombo: boolean
}

/**
 * One stored piece of a composed line - `order_item_parts`, as `BACKEND.md` §10A.337 answers it.
 *
 * Declared here rather than beside `KitchenLine` because `kitchen-items.ts` already imports from
 * this file, and the other direction would be a cycle.
 */
export interface KitchenPart {
  name?: string | null
  quantity?: number | null
}

/**
 * A composed line's composition, read off the **stored parts** rather than out of its own name -
 * `FRONTEND.md` §9.667, owner O51.
 *
 * **The owner**: *"In Sereď - kuchyňa, we dont see zloženie dynamických píz."*
 *
 * **Why the name was never a safe place to read it from.** `parseItemName` above finds the
 * composition inside parentheses, which is a sentence the order builder assembled - and the builder
 * is not the only writer. Measured on the restored data: **521 of 521 of Šurany's custom-pizza lines
 * carry a parenthesised list and a line written without one has nothing for the parse to find**, so
 * the card prints a bare *Vlastná pizza* and a cook has to go and ask. The parts are a column; they
 * are there whoever wrote the line.
 *
 * **It does not replace the parse, it stands behind it.** Where the name carries a list that list is
 * what the till printed and what the customer was shown, so the two cannot be made to disagree by
 * preferring one. This only answers where the parse found nothing.
 *
 * **A count is drawn only when it is more than one**, because *1× Syr* on every chip is noise a cook
 * reads past, and double cheese is exactly the thing they must not.
 */
export function partsDescription(parts: readonly KitchenPart[] | undefined | null): string | null {
  if (!parts || parts.length === 0) return null
  const pieces = parts.flatMap((part) => {
    const name = typeof part.name === 'string' ? part.name.trim() : ''
    if (name === '') return []
    const quantity = typeof part.quantity === 'number' && part.quantity > 1 ? part.quantity : null
    return [quantity === null ? name : `${quantity}× ${name}`]
  })
  return pieces.length === 0 ? null : pieces.join(', ')
}

export function parseItemName(productName: string): ParsedItemName {
  if (!productName) return { name: '–', description: null, extras: null, isKombo: false }

  // §9.430 - through `komboHalvesOf`, which is the same helper both writers build the name with.
  // This used to be a case-sensitive `startsWith('Kombo Pizza:')` and matched only legacy's
  // spelling, so a kombo ordered on the AGOS website fell through to the generic branch and printed
  // whole, with no ingredients. `composed` is false here because this function is given a name and
  // nothing else: a bare `A + B + C` is a kombo only to a caller that knows the line is composed.
  const halves = komboHalvesOf(productName)
  if (halves !== null) {
    return {
      name: KOMBO_NAME_PREFIX,
      description: halves.join(' · '),
      extras: null,
      isKombo: true,
    }
  }

  let extras: string | null = null
  let cleaned = productName

  const bracketOpen = productName.indexOf('[')
  const bracketClose = productName.lastIndexOf(']')
  if (bracketOpen > 0 && bracketClose > bracketOpen) {
    extras = productName.slice(bracketOpen + 1, bracketClose).trim() || null
    cleaned = productName.slice(0, bracketOpen).trim()
  }

  const parenOpen = cleaned.indexOf('(')
  const parenClose = cleaned.lastIndexOf(')')
  if (parenOpen > 0 && parenClose > parenOpen) {
    return {
      name: cleaned.slice(0, parenOpen).trim(),
      description: cleaned.slice(parenOpen + 1, parenClose).trim() || null,
      extras,
      isKombo: false,
    }
  }

  return { name: cleaned, description: null, extras, isKombo: false }
}

export interface MergedIngredient {
  name: string
  count: number
  /** The customer asked for more of this than the recipe has. */
  boosted: boolean
  /** Not in the recipe at all — an addition. */
  standalone: boolean
}

/**
 * A doubled topping with the count in front - `2× Mozzarella syr`, legacy's builder (§9.445, FB-765).
 *
 * **Measured on the owner's data: 52 such pieces across 41 custom-pizza lines.** Before §9.445 nothing
 * parsed it, so the piece went in verbatim as a name and the chip read **`1× 2× Mozzarella syr`** - an
 * ordinary white chip whose badge says one and whose text says two.
 */
const LEADING_COUNT = /^([1-9]\d*)\s*×\s*(.+)$/

/**
 * The same with the count behind - `Kukurica 2×`, which is what **AGOS's own** builder writes - §9.669,
 * FB-994, owner #19.
 *
 * **This was the other half of FB-765 and it was split across two regexes.** The bracketed extras read
 * only this form and the parenthesised composition read only the leading one, so `pizzaLineName`'s
 * `Kukurica 2×` (`pizza-builder.ts`, inside the parentheses) reached the composition parse as a name:
 * a white chip reading `1×` beside `Kukurica 2×`. The owner: *"nech ukazuje 2x kukurica ... nech je to
 * cervenim ako keď je doplnková surovina"*.
 *
 * Whitespace before the number is required, so a name that ends in a digit is not a count; the sign is
 * required in both forms, so `4 sezónne bylinky` is not one either.
 */
const TRAILING_COUNT = /^(.+?)\s+([1-9]\d*)\s*×$/

/**
 * One piece of an ingredient list, with its count read off whichever end carries it - §9.669.
 *
 * **One reader for both lists and both spellings**, because every split of this - one regex per list,
 * one spelling per regex - left a shape that whispered. Lane L3 moves the custom builder to the leading
 * form; every ticket already stored keeps the trailing one, and the kitchen reads both for ever.
 */
export function countedPiece(piece: string): { name: string; count: number } {
  const trimmed = piece.trim()
  const leading = LEADING_COUNT.exec(trimmed)
  if (leading) {
    return { name: (leading[2] ?? trimmed).trim(), count: Number.parseInt(leading[1] ?? '1', 10) }
  }
  const trailing = TRAILING_COUNT.exec(trimmed)
  if (trailing) {
    return { name: (trailing[1] ?? trimmed).trim(), count: Number.parseInt(trailing[2] ?? '1', 10) }
  }
  return { name: trimmed, count: 1 }
}

/**
 * Folds the extras into the recipe.
 *
 * A customer who orders extra ham on a pizza that already has ham should see
 * one chip reading `2× Šunka`, not `1× Šunka` in the recipe and `1× Šunka`
 * underneath — the second is how a cook forgets the extra. Matching is loose
 * (either name containing the other) because the recipe says *Šunka* and the
 * topping list says *Šunka pikantná*.
 *
 * Anything that matches nothing stays separate and is marked `standalone`, so
 * the card can colour it as an addition.
 *
 * **A base changed at the till is not an extra - `FRONTEND.md` §9.731, owner O100.** `Základ: Smotanový základ`
 * is the bracket's first piece. Merged loosely like a topping it would read *Smotanový základ* against a recipe that
 * already has it as **two** cream bases, so it is lifted out: it **replaces** the description's base chip - one whose
 * folded name says *základ*, the word every base on all three franchises carries (*Rajčinový základ*, *Smotanový
 * základ*, Sereď's *Pomodoro pelato základ* and *Smotanovo bryndzový základ*, §9.647) - and is drawn first, as a
 * change. With no such chip it is simply first.
 */
export function mergeIngredientsAndExtras(
  description: string | null,
  extras: string | null,
): MergedIngredient[] {
  const base: MergedIngredient[] = []
  if (description) {
    // Both separators are real: the custom-pizza builder writes `·` between the
    // base and its toppings, and the recipe itself uses commas.
    for (const piece of description.split(/[,·]/)) {
      const trimmed = piece.trim()
      if (!trimmed) continue

      // §9.445 and §9.669 - a `2×` at either end is a double portion of this topping, not part of its
      // name. More than one is the state the owner asked to see, and it is `boosted` rather than
      // `standalone` because it *is* one of the pizza's own toppings - there is just more of it. A
      // piece that says `1×` is one portion and stays an ordinary chip.
      const { name, count } = countedPiece(trimmed)
      base.push({ name, count, boosted: count > 1, standalone: false })
    }
  }

  const standalone: MergedIngredient[] = []
  let chosenBase: MergedIngredient | null = null
  let baseRead = false
  if (extras) {
    for (const piece of extras.split(',')) {
      const trimmed = piece.trim()
      if (!trimmed) continue

      // §9.731 - the base changed at the till, read before anything can merge it as a count. The first base piece
      // decides and an empty one names nothing, as `menuPizzaBaseOf` and the server's `MenuPizzaBase.of` read it.
      if (trimmed.startsWith(MENU_PIZZA_BASE_PREFIX)) {
        if (!baseRead && trimmed.slice(MENU_PIZZA_BASE_PREFIX.length).trim() !== '') {
          chosenBase = { name: trimmed, count: 1, boosted: true, standalone: true }
        }
        baseRead = true
        continue
      }

      // `+Šunka` is how the extras dialog writes one portion; the sign is not part of the name.
      const { name, count } = countedPiece(trimmed.replace(/^\+/, ''))

      const match = base.find(
        (ingredient) =>
          ingredient.name.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(ingredient.name.toLowerCase()),
      )
      if (match) {
        match.count += count
        match.boosted = true
      } else {
        standalone.push({ name, count, boosted: true, standalone: true })
      }
    }
  }

  if (chosenBase === null) return [...base, ...standalone]
  return [chosenBase, ...base.filter((ingredient) => !isBaseChip(ingredient.name)), ...standalone]
}

/** Whether a recipe chip is the pizza's base - §9.731: its name, case and accents folded, says *základ*. */
function isBaseChip(name: string): boolean {
  return name.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase().includes('zaklad')
}

const PIZZA_SIZE =
  /[\s\-–—]+(\d{2,3})\s?cm|[([\s](\d{2,3})\s?cm[)\]\s,]|^(\d{2,3})\s?cm/i
const PIZZA_SIZE_ANYWHERE = /\b(\d{2,3})\s?cm\b/i

/** `Gazdovská – 32 cm` → `32 cm`. §14.6: the number is not one of two fixed sizes. */
export function extractPizzaSize(productName: string): string | null {
  if (!productName) return null
  const match = PIZZA_SIZE.exec(productName)
  const cm = match?.[1] ?? match?.[2] ?? match?.[3]
  if (cm) return `${cm} cm`
  const fallback = PIZZA_SIZE_ANYWHERE.exec(productName)
  return fallback?.[1] ? `${fallback[1]} cm` : null
}

/**
 * Garlic crust gets its own colour on the card, because it is the one topping
 * that changes what the cook does to the dough rather than what goes on top,
 * and it is the one most often missed. Three spellings are in the data.
 */
export function isGarlicCrust(name: string): boolean {
  const lowered = name.trim().toLowerCase()
  return (
    lowered.includes(tt('cesnakové kraje')) ||
    lowered.includes('cesnak. kraje') ||
    lowered.includes('cesnakove kraje')
  )
}

const GIFT_PREFIX = /^🎁\s*(DARČEK:\s*)?/i

export function isGiftItem(productName: string | undefined): boolean {
  return (productName ?? '').startsWith('🎁')
}

/** `🎁 DARČEK: Kofola` → `Kofola`, for matching against the gift rules. */
export function giftNameOf(productName: string | undefined): string {
  return (productName ?? '').replace(GIFT_PREFIX, '').trim()
}

/**
 * How an ingredient chip is drawn — §9.122, the owner's point 8.
 *
 * *"zvýraznit pizza ingrediencie hrubsie svetlejsie aj počet"*. The board is wall-mounted and read
 * across a kitchen; white at 35 % opacity was the **count**, which is the one thing on the chip that
 * changes what the cook makes.
 *
 * Named tiers rather than literals in the JSX, for the reason §9.55 gives about the amount formatter:
 * a rule that only exists inside a component is a rule nobody can check. The whole of this report is
 * about which tier a piece of text is drawn at, so the tiers are the thing worth testing.
 *
 * **Each tier is a light/dark pair** - §9.788, owner O141: the board follows the scheme, so the white
 * the tiers were named for is the `dark:` half, and the light board draws the same tier in slate.
 */
export const CHIP_TIERS = {
  /** Too faint to read at distance. Kept named so a test can assert nothing uses it. */
  muted: 'text-slate-400 dark:text-white/35',
  /** An ordinary ingredient. */
  plain: 'text-slate-800 dark:text-white/85 bg-slate-900/12 dark:bg-white/12',
  /** Its multiplier — brighter than the name was, because the count is what changes the dish. */
  count: 'text-slate-700 dark:text-white/70',
  /** Added on top of the recipe, or doubled. */
  boosted: 'text-red-700 dark:text-red-300',
  standalone: 'text-red-700 dark:text-red-300 bg-red-500/20',
  garlic: 'bg-amber-400 text-amber-950',
} as const

/**
 * How large the kitchen card's text is drawn - §9.672, owner #2.
 *
 * *"Zväčšiť písmo v kuchyni na zložení produktoch ( zle vidia že malé to je ale to hovorili aj u mna )"* - asked from
 * Sereď and from Šurany. **This supersedes §9.122's *"the size stays 10 px"***: §9.122 raised the contrast and kept
 * the size so a card held as many lines as before, and the owner has now asked for the size itself.
 *
 * Named tiers rather than literals in the JSX, for §9.122's own reason - a size that exists only inside a component
 * is a rule nobody can check. The colour tiers above are untouched; only how large things are drawn moves.
 */
export const KITCHEN_TEXT = {
  /** An ingredient chip and its count - the thing the owner named. Was 10 px. */
  chip: 'text-[14px]',
  /** The product's name. Was 13 px. */
  name: 'text-[16px]',
  /** The quantity badge in front of a line. Was 13 px. */
  quantity: 'text-[15px]',
  /** The size badge beside a pizza's name. Was 10 px. */
  size: 'text-[13px]',
  /** A kombo half's name, which heads its own chips. Was 11 px. */
  half: 'text-[14px]',
  /** A label over a group - the gift tag, a box's name, a round's heading, the strip's count. Were 9-10 px. */
  label: 'text-[12px]',
  /** Who it is for - the delivery's customer and town. Were 11 px. */
  note: 'text-[13px]',
  /** The order's own note - §9.810, owner O156: *"poznámka ... výraznejšie, väčšie"*. Was `note`, 13 px. */
  orderNote: 'text-[18px]',
  /** A line's own note under its item - §9.810. Was 14 px. */
  lineNote: 'text-[16px]',
  /** The elapsed time in the ticket's band - §9.810, owner O156's *časenka*. Was 14 px. */
  elapsed: 'text-[22px]',
  /** The preorder countdown beside it, and the scheduled time block - §9.810. Were 11 and 13 px. */
  countdown: 'text-[15px]',
} as const

/**
 * The board's columns - §9.672: one fewer at each width than before, so a card is wide enough for 14 px chips.
 *
 * A wall tablet is typically 1 280 px wide, where the board drew four cards of about 300 px; at the new sizes that
 * wraps nearly every chip. It draws three there now, and four only from 1 536 px.
 */
export const KITCHEN_BOARD_COLUMNS = 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'

/**
 * The chip's own colour - §9.445, FB-765 for the `boosted` case.
 *
 * **The precedence is stated rather than left to argument order.** Garlic first: it is a physical
 * crust and the loudest thing on the card. Then `standalone` and `boosted`, which land on the same
 * red deliberately - to a cook *added on top* and *twice as much* are the same instruction, and a
 * fourth tier would make them look different for no reason anybody could use. A doubled addition is
 * both and is unaffected.
 *
 * **`boosted` was reaching `ingredientCountClasses` alone**, so a 2× of something the recipe already
 * has got a red **digit** on a plain chip while a plain addition got a red **chip** - which is the
 * difference the owner was reading: *"make it more visible, likee the whole row red"*.
 */
export function ingredientChipClasses(state: {
  garlic?: boolean
  standalone?: boolean
  boosted?: boolean
}): string {
  if (state.garlic === true) return `${CHIP_TIERS.garlic} font-black`
  if (state.standalone === true) return `${CHIP_TIERS.standalone} font-black`
  if (state.boosted === true) return `${CHIP_TIERS.standalone} font-black`
  return `${CHIP_TIERS.plain} font-black`
}

export function ingredientCountClasses(state: {
  garlic?: boolean
  boosted?: boolean
}): string {
  if (state.garlic === true) return 'font-black text-amber-950'
  return `font-black ${state.boosted === true ? CHIP_TIERS.boosted : CHIP_TIERS.count}`
}
